import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api/v1',
  // Lista em query sai como `x=a&x=b`, que é o que o FastAPI lê como
  // `list[str]`. O padrão do axios é `x[]=a&x[]=b`, e o FastAPI simplesmente
  // **não enxerga** o parâmetro: nenhum erro, só um filtro que não filtra e
  // uma consulta que volta vazia. Fica na instância, não por chamada, porque
  // o modo de falha é silencioso e reapareceria na próxima lista.
  paramsSerializer: { indexes: null },
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((p) => (error ? p.reject(error) : p.resolve(token)));
  failedQueue = [];
};

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('access_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      // Fila de pendentes: sem isso, várias requisições simultâneas disparariam
      // refreshes concorrentes e uma invalidaria o token da outra.
      if (isRefreshing) {
        return new Promise((resolve, reject) => failedQueue.push({ resolve, reject })).then(
          (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            return api(original);
          }
        );
      }
      original._retry = true;
      isRefreshing = true;

      const refresh = localStorage.getItem('refresh_token');
      if (!refresh) {
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(err);
      }
      try {
        // Usa a mesma base do cliente (nao um caminho relativo fixo): em dev o
        // backend esta noutra porta. `axios` puro de proposito — passar pelo
        // `api` reentraria neste mesmo interceptor.
        const { data } = await axios.post(`${api.defaults.baseURL}/auth/refresh`, {
          refresh_token: refresh,
        });
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        processQueue(null, data.access_token);
        original.headers.Authorization = `Bearer ${data.access_token}`;
        return api(original);
      } catch (e) {
        processQueue(e, null);
        window.dispatchEvent(new Event('auth:logout'));
        return Promise.reject(e);
      } finally {
        isRefreshing = false;
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Extrai a mensagem de erro da API, com fallback legível.
 *
 * A requisição que NÃO CHEGA ao servidor tem texto próprio, de propósito. Sem
 * isso, servidor fora do ar, preflight recusado pelo CORS e erro de validação
 * exibiam a mesma frase — e quem lê a tela procura o defeito no que digitou,
 * quando o backend sequer foi alcançado. Foi assim que um método não liberado
 * no CORS apareceu como "não foi possível salvar".
 */
export function mensagemErro(err, padrao = 'Não foi possível completar a ação.') {
  if (err && !err.response) {
    if (err.code === 'ECONNABORTED') {
      return 'O servidor demorou demais para responder. O que você enviou não é o problema — tente de novo.';
    }
    return 'Não foi possível falar com o servidor: a requisição não chegou até ele. Verifique se o backend está no ar; o que você preencheu não é o problema.';
  }
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.mensagem) return detail.mensagem;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return padrao;
}

export default api;
